import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { restaurantService } from '../services/restaurantService';
import outletService from '../services/outletService';

const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const [restaurantData, setRestaurantData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [loadedServiceType, setLoadedServiceType] = useState(null);
  const [showOutletSelectionModal, setShowOutletSelectionModal] = useState(false);
  const [taxInfo, setTaxInfo] = useState(JSON.parse(sessionStorage.getItem('tax')) || 
    {
    'gst': '',
    'delivery_fee': '',
    'service_charge': '',
    'free_delivery': ''
  })
  const [loginUser,setLoginUser] = useState(JSON.parse(sessionStorage.getItem("user")) || {})


  const [outletsLoaded, setOutletsLoaded] = useState(false);
  const [outletsLoadingAttempted, setOutletsLoadingAttempted] = useState(false);
  const [cart, setCart] = useState([]);

  // Debug modal state changes
  useEffect(() => {
    console.log("Cart in Use Effect => ", cart);
    if (localStorage.getItem("cart_items")) {
      setCart(JSON.parse(localStorage.getItem("cart_items")));
    }
    console.log('RestaurantContext - showOutletSelectionModal changed to:', showOutletSelectionModal);
  }, [showOutletSelectionModal]);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [sliderImages, setSliderImages] = useState([]);

  // Filters and cart state to support HomePage
  const [filters, setFilters] = useState({
    category: null,
    isVeg: null,
    priceRange: [0, 1000],
    sortBy: 'rating',
  });


  // Cart management functions
  const addToCart = useCallback((item, quantity = 1, addons = [], specialInstructions = '', selectedSize = null) => {
    // Calculate price based on selected size
    let price = item.base_price;
    if (selectedSize) {
      price = selectedSize.final_price || selectedSize.price || item.base_price;
    }
    if (addons.length > 0) {
      for (let i = 0; i < addons.length; i++) {
        // console.log(addons[i].price)
        price += addons[i].price;
      }
    }

    // Apply item discount if enabled
    if (item?.discount_enabled && item?.discount_percentage > 0) {
      price = price * (1 - item.discount_percentage / 100);
    }

    const cartItem = {
      id: item.id,
      name: item.name,
      description: item.description,
      image_url: item.image_url,
      base_price: item.base_price,
      price: price, // Use calculated price
      quantity: quantity,
      addons: addons,
      special_instructions: specialInstructions,
      selectedSize: selectedSize, // Store selected size
      is_veg: item.is_veg,
      category_id: item.category_id,
      outlet_id: item.outlet_id
    };

    setCart(prevCart => {
      // Create a unique key for this cart item based on item, size, and addons
      const createItemKey = (item) => {
        const sizeKey = item.selectedSize ? `_size_${item.selectedSize.name}` : '';
        const addonsKey = item.addons && item.addons.length > 0
          ? `_addons_${item.addons.map(a => `${a.name}_${a.quantity}`).join('_')}`
          : '';
        return `${item.id}${sizeKey}${addonsKey}`;

      };



      const newItemKey = createItemKey(cartItem);
      const existingItemIndex = prevCart.findIndex(cartItem => createItemKey(cartItem) === newItemKey);
      console.log("Cart")

      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + quantity
        };
        localStorage.setItem("cart_items", JSON.stringify([updatedCart]));
        return updatedCart;
      } else {
        // Add new item
        localStorage.setItem("cart_items", JSON.stringify([...prevCart, cartItem]));
        return [...prevCart, cartItem];
      }
    });
  }, []);

  const updateCartItem = useCallback((itemId, updates) => {
    setCart((prevCart) => {
      const updateValue = prevCart.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
      localStorage.setItem("cart_items", JSON.stringify(updateValue));
      return updateValue
    }

    );
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCart((prevCart) => {
      const updatedCart = prevCart.filter(item => item.id !== itemId);
      localStorage.setItem("cart_items", JSON.stringify(updatedCart));
      return updatedCart;
    });
  }, []);

  const clearCart = useCallback(() => {
    localStorage.removeItem("cart_items");
    setCart([]);
  }, []);

  const getCartItemCount = useCallback(() => {
    return cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }, [cart]);

  const getCartTotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + ((item.price || item.base_price || 0) * (item.quantity || 1)), 0);
  }, [cart]);

  const [loading, setLoading] = useState(true);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [menuItemsLoading, setMenuItemsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRestaurantData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await restaurantService.getPublicRestaurantData();
      setRestaurantData(data);

      
      const sessionOutletId = sessionStorage.getItem('selectedOutletId');

      // Load categories, menu items, outlets, and slider images
      const [cats, items, outletData, sliderData] = await Promise.allSettled([
        restaurantService.getPublicCategories(),
        restaurantService.getPublicMenuItems(),
        restaurantService.getPublicOutlets(),
        restaurantService.getPublicSliderImages(),
      ]);

      setCategories(cats.status === 'fulfilled' ? (cats.value || []) : []);
      setMenuItems(items.status === 'fulfilled' ? (items.value || []) : []);
      setOutlets(outletData.status === 'fulfilled' ? (outletData.value || []) : []);
      setOutletsLoaded(true); // Mark outlets as loaded
      setOutletsLoadingAttempted(true); // Mark that we've attempted to load outlets

      // Debug slider images loading
      if (sliderData.status === 'fulfilled') {
        console.log('Slider images loaded successfully:', sliderData.value);
        setSliderImages(sliderData.value || []);
      } else {
        console.error('Failed to load slider images:', sliderData.reason);
        setSliderImages([]);
      }
      
      // Check if outlet is selected in current session
      // const sessionOutletId = sessionStorage.getItem('selectedOutletId');
      console.log('RestaurantContext - sessionOutletId:', sessionOutletId);
      console.log('RestaurantContext - outletData:', outletData.value);
      console.log('RestaurantContext - outletData.length:', outletData.value?.length);

      // Ensure we have valid outlet data
      const validOutlets = outletData && Array.isArray(outletData.value) ? outletData.value : [];
      console.log('RestaurantContext - validOutlets:', validOutlets);
      console.log('RestaurantContext - validOutlets.length:', validOutlets.length);

      if (validOutlets.length > 0) {
        const sessionOutlet = sessionOutletId ? validOutlets.find(o => o.id === parseInt(sessionOutletId)) : null;
        console.log('RestaurantContext - sessionOutlet:', sessionOutlet);
        
        if (sessionOutlet) {
          // Use session outlet if it exists and is valid
          console.log('RestaurantContext - Using session outlet:', sessionOutlet.name);
          setSelectedOutlet(sessionOutlet);
        } else if (validOutlets.length === 1) {
          // Only one outlet available - auto-select it
          console.log('RestaurantContext - Auto-selecting single outlet:', validOutlets[0].name);
          setSelectedOutlet(validOutlets[0]);
          sessionStorage.setItem('selectedOutletId', validOutlets[0].id.toString());
        } else {
          // Multiple outlets - show selection modal
          console.log('RestaurantContext - Setting outlet selection modal to true - multiple outlets available');
          setIsFirstVisit(true);
          setShowOutletSelectionModal(true);
        }
      } else {
        console.log('RestaurantContext - No valid outlet data available');
      }
    } catch (err) {
      setError('Failed to load restaurant data');
      console.error('Error loading restaurant data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load menu items for specific outlet
  const loadMenuItemsForOutlet = useCallback(async (outletId) => {
    try {
      setMenuItemsLoading(true);
      const items = await restaurantService.getPublicMenuItems({ outlet_id: outletId });
      setMenuItems(items || []);
    } catch (err) {
      console.error('Error loading menu items for outlet:', err);
      setError('Failed to load menu items for selected outlet');
    } finally {
      setMenuItemsLoading(false);
    }
  }, []);

  // Load outlets by service type
  const loadOutletsByService = useCallback(async (serviceType) => {
    // If we've already attempted to load outlets and have results, return them
    if (outletsLoadingAttempted && outletsLoaded) {
      return outlets;
    }

    // Don't reload if we already have outlets for this service type
    if (loadedServiceType === serviceType && outlets.length > 0) {
      return outlets;
    }

    // Prevent multiple simultaneous calls
    if (outletsLoading) {
      return outlets;
    }

    try {
      setOutletsLoading(true);
      setOutletsLoadingAttempted(true);

      // Clear existing outlets when switching service types
      if (loadedServiceType !== serviceType) {
        setOutlets([]);
        setSelectedOutlet(null);
      }

      const outletData = await restaurantService.getPublicOutlets(serviceType);
      console.log(outletData)
      setOutlets(outletData || []);
      setLoadedServiceType(serviceType);
      setOutletsLoaded(true);

      // Auto-select outlet if available
      if (outletData && outletData.length > 0) {
       
        // Check sessionStorage for previously selected outlet
        const sessionOutletId = sessionStorage.getItem('selectedOutletId');
        const sessionOutlet = sessionOutletId ? outletData.find(o => o.id === parseInt(sessionOutletId)) : null;

        if (sessionOutlet) {
          // Use session outlet if it's available for this service
          setSelectedOutlet(sessionOutlet);
        } else if (outletData.length === 1) {
          // Only one outlet available - auto-select it
          setSelectedOutlet(outletData[0]);
          sessionStorage.setItem('selectedOutletId', outletData[0].id.toString());
        } else {
          // Multiple outlets - don't auto-select, let user choose
          // Don't set any outlet, let the main loadRestaurantData handle the modal
        }
      }
      return outletData;
    } catch (err) {
      console.error('Error loading outlets by service:', err);
      setOutletsLoaded(true); // Mark as loaded even on error to prevent retries
      return [];
    } finally {
      setOutletsLoading(false);
    }
  }, [loadedServiceType, outlets.length, outletsLoading, outletsLoadingAttempted, outletsLoaded]);

  const updateRestaurantData = async (configData) => {
    try {
      setLoading(true);
      const updatedData = await restaurantService.updateRestaurantConfig(configData);
      setRestaurantData(updatedData);
      return updatedData;
    } catch (err) {
      setError('Failed to update restaurant data');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshRestaurantData = () => {
    loadRestaurantData();
  };

  useEffect(() => {
    loadRestaurantData();
  }, []);

  // Fallback: Ensure modal shows if we have outlets but no selected outlet after loading
  useEffect(() => {
    if (!loading && outletsLoaded && outlets.length > 0 && !selectedOutlet && !showOutletSelectionModal) {
      console.log('RestaurantContext - Fallback: Showing outlet selection modal');
      setIsFirstVisit(true);
      setShowOutletSelectionModal(true);
    }
  }, [loading, outletsLoaded, outlets.length, selectedOutlet, showOutletSelectionModal]);


  // Derived helpers expected by HomePage
  const getFilteredMenuItems = useCallback(() => {
    let items = [...menuItems];

    // Apply outlet filter - only show items from selected outlet
    if (selectedOutlet) {
      items = items.filter(i => {
        // Check if item is available for this outlet
        if (i.outlet_id && i.outlet_id !== selectedOutlet.id) return false;
        // Check if item is available
        return i.is_available !== false;
      });
    }

    // Apply category filter
    if (filters.category) {
      items = items.filter(i => i.category_id === filters.category);
    }

    // Apply veg filter
    if (filters.isVeg !== null) {
      items = items.filter(i => Boolean(i.is_veg) === Boolean(filters.isVeg));
    }

    // Apply price range filter
    if (filters.priceRange && filters.priceRange.length === 2) {
      const [min, max] = filters.priceRange;
      items = items.filter(i => {
        const basePrice = i.base_price || 0;
        return basePrice >= min && basePrice <= max;
      });
    }

    // Apply sorting
    if (filters.sortBy === 'price') {
      items.sort((a, b) => (a.base_price || 0) - (b.base_price || 0));
    } else if (filters.sortBy === 'rating') {
      items.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (filters.sortBy === 'name') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    }

    return items;
  }, [menuItems, filters, selectedOutlet]);



  // Outlet management
  const selectOutlet = (outlet) => {
    setSelectedOutlet(outlet);
    // Save to sessionStorage for session persistence
    if (outlet && outlet.id) {
      sessionStorage.setItem('selectedOutletId', outlet.id.toString());

      const getTax = async (outlet_id) => {
        try {
          const response = await outletService.getTaxDetails(outlet_id);
          sessionStorage.setItem('tax', JSON.stringify(response));
          setTaxInfo(response)
        }
        catch (error) {
          console.error("Error on Getting Tax => ", error)
        }

      }
      // Getting TaxDetails for Outlets
      getTax(outlet.id)
      // Load menu items specific to this outlet

      loadMenuItemsForOutlet(outlet.id);
    }
  };

  // Handle outlet selection from modal
  const handleOutletSelection = (outlet) => {
    selectOutlet(outlet);
    setShowOutletSelectionModal(false);
    setIsFirstVisit(false);
  };

  // Show outlet selection modal
  const showOutletSelection = () => {
    setShowOutletSelectionModal(true);
  };

  // Close outlet selection modal
  const closeOutletSelection = () => {
    setShowOutletSelectionModal(false);
    // If this was a first visit and no outlet was selected, select the first available
    if (isFirstVisit && !selectedOutlet && outlets.length > 0) {
      selectOutlet(outlets[0]);
    }
    setIsFirstVisit(false);
  };

  const clearOutlets = useCallback(() => {
    setOutlets([]);
    setSelectedOutlet(null);
    setLoadedServiceType(null);
    setOutletsLoading(false);
  }, []);

  const forceReloadOutlets = useCallback(async (serviceType) => {
    setLoadedServiceType(null);
    setOutlets([]);
    setSelectedOutlet(null);
    setOutletsLoading(false);
    return await loadOutletsByService(serviceType);
  }, [loadOutletsByService]);

  const isOutletsLoadingForService = useCallback((serviceType) => {
    return outletsLoading && loadedServiceType === serviceType;
  }, [outletsLoading, loadedServiceType]);

  const getCurrentOutletsServiceType = useCallback(() => {
    return loadedServiceType;
  }, [loadedServiceType]);

  const getOutletById = (outletId) => {
    return outlets.find(outlet => outlet.id === outletId);
  };

  const taxDetails = 77;
  const getOutletMenuItems = (outletId) => {
    if (!outletId) return menuItems;
    return menuItems.filter(item => {
      // Check if item is available for this outlet
      if (item.outlet_id && item.outlet_id !== outletId) return false;
      // Check if item is available
      return item.is_available !== false;
    });
  };

  const getCurrentOutletMenuItems = useCallback(() => {
    if (!selectedOutlet) return [];
    return getOutletMenuItems(selectedOutlet.id);
  }, [selectedOutlet, getOutletMenuItems]);

  // Check delivery availability for current outlet
  const isDeliveryAvailable = useCallback(() => {
    // Check multiple possible field names for delivery availability
    const deliveryAvailable = selectedOutlet?.is_delivery_available === true ||
      selectedOutlet?.delivery_enabled === true ||
      selectedOutlet?.delivery_available === true ||
      selectedOutlet?.delivery === true;

    // If no specific field is found, assume delivery is available (fallback)
    return deliveryAvailable !== undefined ? deliveryAvailable : true;
  }, [selectedOutlet]);

  // Check takeaway availability for current outlet
  const isTakeawayAvailable = useCallback(() => {
    // Check multiple possible field names for takeaway availability
    const takeawayAvailable = selectedOutlet?.is_takeaway_available === true ||
      selectedOutlet?.takeaway_enabled === true ||
      selectedOutlet?.takeaway_available === true ||
      selectedOutlet?.takeaway === true;

    // If no specific field is found, assume takeaway is available (fallback)
    return takeawayAvailable !== undefined ? takeawayAvailable : true;
  }, [selectedOutlet]);

  // Check dine-in availability for current outlet
  const isDineInAvailable = useCallback(() => {
    // Check multiple possible field names for dine-in availability
    const dineInAvailable = selectedOutlet?.is_dine_in_available === true ||
      selectedOutlet?.dine_in_enabled === true ||
      selectedOutlet?.dine_in_available === true ||
      selectedOutlet?.dine_in === true;

    // If no specific field is found, assume dine-in is available (fallback)
    return dineInAvailable !== undefined ? dineInAvailable : true;
  }, [selectedOutlet]);

  // Check table reservation availability for current outlet
  const isTableReservationAvailable = useCallback(() => {
    return selectedOutlet?.is_table_reservation_available === true;
  }, [selectedOutlet]);

  // Get available service types for current outlet
  const getAvailableServiceTypes = useCallback(() => {
    const services = [];
    if (isDeliveryAvailable()) services.push('delivery');
    if (isTakeawayAvailable()) services.push('takeaway');
    if (isDineInAvailable()) services.push('dine_in');
    return services;
  }, [isDeliveryAvailable, isTakeawayAvailable, isDineInAvailable]);

  const value = {
    restaurantData,
    restaurant: restaurantData || {},
    categories,
    menuItems,
    outlets,
    selectedOutlet,
    selectOutlet,
    loadMenuItemsForOutlet,
    clearOutlets,
    forceReloadOutlets,
    isOutletsLoadingForService,
    getCurrentOutletsServiceType,
    getOutletById,
    getOutletMenuItems,
    getCurrentOutletMenuItems,
    isDeliveryAvailable,
    isTakeawayAvailable,
    isDineInAvailable,
    isTableReservationAvailable,
    getAvailableServiceTypes,
    loadOutletsByService,
    filters,
    setFilters,
    cart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getFilteredMenuItems,
    getCartItemCount,
    getCartTotal,
    loading,
    outletsLoading,
    outletsLoaded,
    outletsLoadingAttempted,
    menuItemsLoading,
    error,
    loadRestaurantData,
    updateRestaurantData,
    refreshRestaurantData,
    // Outlet selection modal
    showOutletSelectionModal,
    isFirstVisit,
    handleOutletSelection,
    showOutletSelection,
    closeOutletSelection,
    // Slider images
    sliderImages,
    taxDetails,
    taxInfo,
    loginUser,
    setLoginUser,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
